import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Select from 'flarum/common/components/Select';
import Button from 'flarum/common/components/Button';
import PageStructure from 'flarum/forum/components/PageStructure';
import WikiIndexSidebar from './WikiIndexSidebar';
import { tr } from '../utils/translate';
import { basePath, BASE_PATH, articleHref, showError, fullWidth, pageClassName, emptySidebar, mobileTitle } from '../utils/helpers';
import { canCreateWikiArticle } from '../utils/permissions';
import { loadArticle, loadCategories, createArticle, updateArticle } from '../utils/api';
import { wikiComposerAvailable, wikiComposerOpenFor, openWikiComposer, wikiComposerPreview } from '../utils/composer';

/**
 * Create a new article (/wiki/new) or edit an existing one (/wiki/:id/edit).
 * Title + optional category live on the page; the body is written in Flarum's
 * real docked composer (full width, with the editor toolbar / rich text /
 * upload) -- the same UX as a normal discussion reply.
 */
export default class WikiComposePage extends Page {
  constructor() {
    super(...arguments);
    _defineProperty(this, "loading", true);
    _defineProperty(this, "loadError", false);
    _defineProperty(this, "saving", false);
    _defineProperty(this, "editing", false);
    _defineProperty(this, "article", null);
    _defineProperty(this, "categories", []);
    _defineProperty(this, "title", '');
    _defineProperty(this, "slug", '');
    _defineProperty(this, "position", '');
    _defineProperty(this, "isDraft", false);
    _defineProperty(this, "body", '');
    _defineProperty(this, "categoryId", '');
    // FAQ entries being edited. `uid` is a client-side key so removals don't
    // make mithril reuse the wrong entry's inputs.
    _defineProperty(this, "faq", []);
    _defineProperty(this, "_faqUid", 0);
  }
  oninit(vnode) {
    super.oninit(vnode);
    const id = m.route.param('id');
    this.editing = !!id;
    try {
      app.setTitle(this.editing ? tr('compose.title_edit', 'Edit article') : tr('compose.title', 'New article'));
    } catch (e) {}
    const work = [loadCategories().then(cats => {
      this.categories = cats || [];
    }).catch(() => {})];
    if (this.editing) {
      work.push(loadArticle(id).then(article => {
        this.article = article;
        this.title = article.title() || '';
        this.slug = article.slug && article.slug() || '';
        this.isDraft = !!(article.isDraft && article.isDraft());
        const pos = article.position && article.position();
        this.position = pos === null || pos === undefined ? '' : String(pos);
        this.body = article.content() || '';
        this.faq = (article.faq && article.faq() || []).map(entry => ({
          uid: this._faqUid++,
          question: entry && entry.question || '',
          answer: entry && entry.answer || ''
        }));
        const cat = article.category && article.category();
        this.categoryId = cat ? String(cat.id()) : '';
      }).catch(err => {
        this.loadError = true;
        console.error('[linkrobins/wiki] could not load article for edit:', err);
      }));
    } else if (!canCreateWikiArticle()) {
      this.loadError = true;
    }
    Promise.all(work).then(() => {
      this.loading = false;
      m.redraw();
    });
  }
  onremove() {
    // Close our composer if the user navigates away mid-draft.
    try {
      if (wikiComposerOpenFor(this._context()) && app.composer && app.composer.close) app.composer.close();
    } catch (e) {}
  }
  _context() {
    return this.editing ? 'article-edit-' + m.route.param('id') : 'article-new';
  }
  view() {
    return m(PageStructure, {
      className: pageClassName('IndexPage LinkRobinsWiki-page LinkRobinsWiki-page--compose'),
      sidebar: fullWidth() ? emptySidebar : () => {
        try {
          return m(WikiIndexSidebar, {
            className: 'LinkRobinsWiki-sidebar',
            activeCategory: this.categoryId || null
          });
        } catch (e) {
          return null;
        }
      }
    }, m('div', {
      className: 'LinkRobinsWiki-container'
    }, [mobileTitle(), this._renderContent()]));
  }
  _renderContent() {
    if (this.loading) {
      return m(LoadingIndicator);
    }
    if (this.loadError) {
      return m('div', {
        className: 'LinkRobinsWiki-empty'
      }, tr('errors.load_article', 'Could not load this article.'));
    }
    const categoryOptions = {
      '': tr('compose.no_category', 'No category')
    };
    this.categories.forEach(cat => {
      categoryOptions[String(cat.id())] = cat.name();
    });
    const composerOpen = wikiComposerOpenFor(this._context());
    return [m('header', {
      className: 'LinkRobinsWiki-header'
    }, [m('h1', {
      className: 'LinkRobinsWiki-title'
    }, this.editing ? tr('compose.title_edit', 'Edit article') : tr('compose.title', 'New article'))]), m('div', {
      className: 'LinkRobinsWiki-form'
    }, [m('div', {
      className: 'Form-group'
    }, [m('label', tr('compose.title_label', 'Title')), m('input', {
      className: 'FormControl',
      value: this.title,
      disabled: this.saving,
      placeholder: tr('compose.title_placeholder', 'Article title'),
      oninput: e => {
        this.title = e.target.value;
      }
    })]), m('div', {
      className: 'Form-group'
    }, [m('label', tr('compose.slug_label', 'URL slug')), m('input', {
      className: 'FormControl',
      value: this.slug,
      disabled: this.saving,
      placeholder: tr('compose.slug_placeholder', 'e.g. getting-started'),
      oninput: e => {
        this.slug = e.target.value;
      }
    }), m('div', {
      className: 'helpText'
    }, tr('compose.slug_help', "Used in the article's URL. Leave blank to generate it from the title."))]), m('div', {
      className: 'Form-group LinkRobinsWiki-draftToggle'
    }, [m('label', {
      className: 'checkbox'
    }, [m('input', {
      type: 'checkbox',
      checked: this.isDraft,
      disabled: this.saving,
      onchange: e => {
        this.isDraft = e.target.checked;
      }
    }), ' ', tr('compose.draft_label', 'Save as a draft')]), m('div', {
      className: 'helpText'
    }, tr('compose.draft_help', 'A draft is visible only to you and to wiki editors until you publish it.'))]), m('div', {
      className: 'Form-group'
    }, [m('label', tr('compose.position_label', 'Position')), m('input', {
      className: 'FormControl',
      type: 'number',
      value: this.position,
      disabled: this.saving,
      placeholder: tr('compose.position_placeholder', 'e.g. 1'),
      oninput: e => {
        this.position = e.target.value;
      }
    }), m('div', {
      className: 'helpText'
    }, tr('compose.position_help', 'Orders this article within its category, lowest first. Leave blank and it sits after the ordered ones, newest first.'))]), this.categories.length ? m('div', {
      className: 'Form-group'
    }, [m('label', tr('compose.category_label', 'Category')), m(Select, {
      options: categoryOptions,
      value: this.categoryId,
      disabled: this.saving,
      onchange: v => {
        this.categoryId = v;
      }
    })]) : null, m('div', {
      className: 'Form-group'
    }, [m('label', tr('compose.body_label', 'Content')), wikiComposerAvailable() ? m('div', {
      className: 'LinkRobinsWiki-composePreview'
    }, wikiComposerPreview({
      composing: composerOpen,
      placeholder: this.body ? tr('compose.body_placeholder_edit', 'Click to continue editing…') : tr('compose.body_placeholder_click', 'Click to write the article…'),
      onclick: () => this._openComposer()
    })) : this._renderTextareaFallback()]), this._renderFaqEditor(),
    // With the docked composer the submit lives inside it; only the
    // fallback needs page-level Save/Cancel buttons.
    !wikiComposerAvailable() ? m('div', {
      className: 'Form-group LinkRobinsWiki-formActions'
    }, [m(Button, {
      className: 'Button Button--primary',
      loading: this.saving,
      disabled: this.saving,
      onclick: () => this._submit()
    }, this.editing ? tr('compose.submit_update', 'Save changes') : tr('compose.submit_create', 'Publish article')), m(Button, {
      className: 'Button',
      onclick: () => this._cancel()
    }, tr('action.cancel', 'Cancel'))]) : m('div', {
      className: 'LinkRobinsWiki-formActions'
    }, m(Button, {
      className: 'Button',
      onclick: () => this._cancel()
    }, tr('action.cancel', 'Cancel')))])];
  }

  // Optional Q&A pairs rendered as an accordion under the article. Kept out
  // of the docked composer on purpose: they're structured fields, not body
  // text, and most articles won't have any.
  _renderFaqEditor() {
    return m('div', {
      className: 'Form-group LinkRobinsWiki-faqEditor'
    }, [m('label', tr('compose.faq_heading', 'FAQ')), m('div', {
      className: 'helpText'
    }, tr('compose.faq_help', 'Optional questions and answers shown under the article. Markdown is supported in answers.')), this.faq.map((entry, index) => m('div', {
      className: 'LinkRobinsWiki-faqEditor-entry',
      key: 'faq-' + entry.uid
    }, [m('input', {
      className: 'FormControl',
      value: entry.question,
      disabled: this.saving,
      placeholder: tr('compose.faq_question_placeholder', 'Question'),
      oninput: e => {
        entry.question = e.target.value;
      }
    }), m('textarea', {
      className: 'FormControl',
      rows: 3,
      value: entry.answer,
      disabled: this.saving,
      placeholder: tr('compose.faq_answer_placeholder', 'Answer. Markdown is supported.'),
      oninput: e => {
        entry.answer = e.target.value;
      }
    }), m('button', {
      type: 'button',
      className: 'LinkRobinsWiki-faqEditor-remove',
      disabled: this.saving,
      onclick: () => {
        this.faq.splice(index, 1);
      }
    }, [m('i', {
      className: 'fas fa-times',
      'aria-hidden': 'true'
    }), ' ', tr('compose.faq_remove', 'Remove question')])])), m(Button, {
      className: 'Button LinkRobinsWiki-faqEditor-add',
      icon: 'fas fa-plus',
      disabled: this.saving,
      onclick: () => {
        this.faq.push({
          uid: this._faqUid++,
          question: '',
          answer: ''
        });
      }
    }, tr('compose.faq_add', 'Add question'))]);
  }
  _renderTextareaFallback() {
    return m('textarea', {
      className: 'FormControl',
      rows: 12,
      value: this.body,
      disabled: this.saving,
      placeholder: tr('compose.body_placeholder', 'Write the article. Markdown is supported.'),
      oninput: e => {
        this.body = e.target.value;
      }
    });
  }
  _openComposer() {
    const cat = this.categoryId ? this.categories.find(c => String(c.id()) === this.categoryId) : null;
    openWikiComposer({
      wikiContext: this._context(),
      className: 'LinkRobinsWiki-articleComposer',
      placeholder: tr('compose.body_placeholder', 'Write the article. Markdown is supported.'),
      submitLabel: this.editing ? tr('compose.submit_update', 'Save changes') : tr('compose.submit_create', 'Publish article'),
      confirmExit: tr('compose.discard_confirm', 'You have an unsaved article. Discard it?'),
      originalContent: this.body || '',
      wikiHeaderItems: () => [{
        name: 'title',
        content: m('h3', {
          className: 'LinkRobinsWiki-composerTitle'
        }, [m('i', {
          className: cat && cat.icon() || 'fas fa-book'
        }), ' ', this.title || tr('compose.title', 'New article'), cat && cat.name() ? m('span', {
          className: 'LinkRobinsWiki-composerTitle-cat'
        }, ' · ' + cat.name()) : null])
      }],
      onWikiSubmit: (content, body) => this._submit(content, body)
    });
  }
  _submit(content, body) {
    if (this.saving) return;
    const title = (this.title || '').trim();
    const bodyText = (typeof content === 'string' ? content : this.body || '').trim();
    if (!title) {
      showError(tr('errors.title_required', 'Please enter a title.'));
      return;
    }
    if (!bodyText) {
      showError(tr('errors.empty_body', 'Please write the article body before submitting.'));
      return;
    }
    const category = this.categoryId ? app.store.getById('linkrobins-wiki-categories', this.categoryId) || null : null;
    this.saving = true;
    if (body) body.loading = true;
    m.redraw();
    const slug = (this.slug || '').trim();
    // Blank clears the manual order rather than sending 0, which would mean
    // "first".
    const positionRaw = (this.position || '').trim();
    const position = positionRaw === '' ? null : parseInt(positionRaw, 10);
    const isDraft = !!this.isDraft;
    const faq = this.faq.map(entry => ({
      question: (entry.question || '').trim(),
      answer: (entry.answer || '').trim()
    })).filter(entry => entry.question && entry.answer);
    const done = article => {
      this.saving = false;
      this.body = '';
      if (body && body.composer) body.composer.hide();
      m.route.set(articleHref(article));
    };
    const fail = err => {
      this.saving = false;
      if (body) body.loading = false;
      // Prefer the server's message: slug conflicts and the like come back as
      // a translated JSON:API error detail that tells the user what to fix.
      let message = tr('errors.submit', 'Could not save the article.');
      try {
        const detail = err && err.response && err.response.errors && err.response.errors[0] && err.response.errors[0].detail;
        if (detail) message = detail;
      } catch (e) {}
      showError(message);
      console.error('[linkrobins/wiki] save failed:', err);
      m.redraw();
    };
    if (this.editing && this.article) {
      updateArticle(this.article, {
        title,
        slug,
        position,
        isDraft,
        faq,
        content: bodyText,
        relationships: {
          category
        }
      }).then(done).catch(fail);
    } else {
      createArticle(title, bodyText, category, slug, faq, position, isDraft).then(done).catch(fail);
    }
  }
  _cancel() {
    try {
      if (wikiComposerOpenFor(this._context()) && app.composer && app.composer.close) app.composer.close();
    } catch (e) {}
    if (this.editing && this.article) {
      m.route.set(articleHref(this.article));
    } else {
      m.route.set(basePath() + BASE_PATH);
    }
  }
}
flarum.reg.add('linkrobins-wiki', 'forum/components/WikiComposePage', WikiComposePage);